import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { getSEOByPath, defaultSEO } from '../config/seo-config'

const SITE_URL = 'https://avelia.chat'

function normalizePathname(pathname = '/') {
  if (!pathname || pathname === '/') {
    return '/'
  }

  return `${pathname.replace(/\/+$/, '')}/`
}

function toCanonicalUrl(value = '/') {
  const url = new URL(value, SITE_URL)
  const normalizedPathname = normalizePathname(url.pathname)
  return `${url.origin}${normalizedPathname}${url.search}${url.hash}`
}

export function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  twitterTitle,
  twitterDescription,
  children
}) {
  const location = useLocation()
  const pathConfig = getSEOByPath(location.pathname) || {}
  const canonicalUrl = toCanonicalUrl(location.pathname)

  const finalTitle = title || pathConfig.title || defaultSEO.title
  const finalDescription = description || pathConfig.description || defaultSEO.description
  const finalKeywords = keywords || pathConfig.keywords || defaultSEO.keywords
  const finalOgTitle = ogTitle || pathConfig.ogTitle || finalTitle
  const finalOgDescription = ogDescription || pathConfig.ogDescription || finalDescription
  const finalOgUrl = toCanonicalUrl(ogUrl || pathConfig.ogUrl || location.pathname)
  const finalOgImage = ogImage || pathConfig.ogImage || defaultSEO.ogImage
  const finalTwitterTitle = twitterTitle || pathConfig.twitterTitle || finalOgTitle
  const finalTwitterDescription = twitterDescription || pathConfig.twitterDescription || finalOgDescription
  const formattedTitle = finalTitle.includes('Avelia') ? finalTitle : `${finalTitle} | Avelia`

  return (
    <Helmet prioritizeSeoTags>
      <title>{formattedTitle}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content="index,follow,max-image-preview:large" />

      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:url" content={finalOgUrl} />
      {finalOgImage && <meta property="og:image" content={finalOgImage} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Avelia" />
      <meta property="og:locale" content="zh_CN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      {finalOgImage && <meta name="twitter:image" content={finalOgImage} />}

      {children}
    </Helmet>
  )
}

export function useSEO(seoProps = {}) {
  const location = useLocation()
  const pathConfig = getSEOByPath(location.pathname)

  return {
    ...pathConfig,
    ...seoProps
  }
}

export default SEO